/**
 * @typedef {Object} ApiErrorBody
 * @property {string} [error] - Primary error message
 * @property {string} [message] - Alternative error message
 * @property {string} [detail] - Detail message
 * @property {string} [error_detail] - Detailed error (e.g. table-columns)
 * @property {string} [manual_query] - Suggested query when columns fail
 */

/**
 * @typedef {Error & { response?: { data?: ApiErrorBody; status?: number } }} AxiosError
 */

/**
 * Get the best available error message from an API error (axios) or Error.
 * Use in catch blocks: getErrorMessage(err, 'Fallback')
 * @param {unknown} err - Error from catch (AxiosError has err.response?.data?.error)
 * @param {string} [fallback='An error occurred'] - Fallback message if nothing found
 * @returns {string}
 */
export function getErrorMessage(err, fallback = 'An error occurred') {
  if (!err) return fallback;
  // Axios puts response on error for 4xx/5xx; always check for API error body first
  const response = err?.response;
  const data = response?.data;
  if (data && typeof data === 'object') {
    if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    if (typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
    if (Array.isArray(data.detail)) return data.detail.map((d) => (d?.msg ?? String(d))).join('. ');
  }
  const msg = err?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return fallback;
}
